/**
 * Thin typed fetch client for the EduFadder API, for the web app. Unlike the
 * extension (apps/extension/src/lib/api.ts), the web app authenticates
 * purely via HttpOnly cookies — it never sends `X-EduFadder-Client`, so
 * /auth/login, /auth/register, and /auth/refresh never echo the raw refresh
 * token back in the JSON body (see services/api/app/routers/auth.py
 * `_is_extension_client`), which keeps a reflected-XSS bug on this app from
 * being able to read a refresh token out of a response it was never put in.
 * Every request uses `credentials: "include"` so the browser attaches and
 * stores those cookies even though the dev server (5173) and the API (8000)
 * are different origins (they're still "same-site", so SameSite=Lax cookies
 * flow normally — see services/api/app/core/cookies.py).
 */
import type {
  AdminUser,
  AnalyzeRequest,
  AnalyzeResponse,
  ApiErrorBody,
  Claim,
  EvidenceVersion,
  MeResponse,
  Organization,
  OrgAnalyticsSummary,
  OrgMember,
  Release,
  ServerDefaults,
  Source,
  UserSettings,
  AuditLogEntry,
  FeedbackItem,
  AdminInsights,
  ExtensionRelease,
  MeDashboard,
  PassageCheckResponse,
  CuratorReading,
  PublicEdufad,
  PublicEdufadSummary,
  ReadingSearchResult,
  PublicStats,
  Suggestion,
} from "@edufadder/shared-types";

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function parseErrorBody(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return new ApiError(res.status, body.error.code, body.error.message, body.error.details);
  } catch {
    return new ApiError(res.status, "unknown_error", `Request failed with status ${res.status}`);
  }
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing === null) {
    refreshing = fetch(`${API_BASE_URL}/auth/refresh`, { method: "POST", credentials: "include" })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set false for calls (like the refresh attempt itself) that must never recurse. */
  retryOn401?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, retryOn401 = true } = options;
  const doFetch = () =>
    fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res = await doFetch();
  if (res.status === 401 && retryOn401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await doFetch();
    }
  }
  if (!res.ok) throw await parseErrorBody(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Auth -------------------------------------------------------------

export function register(email: string, password: string, displayName: string): Promise<{ access_token: string }> {
  return request("/auth/register", { method: "POST", body: { email, password, display_name: displayName } });
}

export function login(email: string, password: string): Promise<{ access_token: string }> {
  return request("/auth/login", { method: "POST", body: { email, password } });
}

export function logout(): Promise<void> {
  return request("/auth/logout", { method: "POST", retryOn401: false });
}

export function forgotPassword(email: string): Promise<{ message: string; dev_only_reset_token?: string }> {
  return request("/auth/forgot-password", { method: "POST", body: { email }, retryOn401: false });
}

export function resetPassword(token: string, newPassword: string): Promise<void> {
  return request("/auth/reset-password", { method: "POST", body: { token, new_password: newPassword }, retryOn401: false });
}

// --- Me -----------------------------------------------------------------

export function getMe(): Promise<MeResponse> {
  // Deliberately allows the default retry-on-401: the access token cookie has
  // only a 15-minute TTL (see services/api/app/core/config.py), so without
  // this, every page load after that window would look logged-out even
  // though the longer-lived refresh token cookie is still valid.
  return request("/me");
}

export function getMySettings(): Promise<UserSettings> {
  return request("/me/settings");
}

export function patchMySettings(patch: Partial<UserSettings>): Promise<UserSettings> {
  return request("/me/settings", { method: "PATCH", body: patch });
}

export function getServerDefaults(): Promise<ServerDefaults> {
  return request("/me/server-defaults");
}

export function deleteMyAccount(): Promise<void> {
  return request("/me/", { method: "DELETE" });
}

// --- Research consent ----------------------------------------------------

export interface ResearchStatus {
  platform_permits_research_telemetry: boolean;
  has_granted_consent: boolean;
  active: boolean;
}

export function getResearchStatus(): Promise<ResearchStatus> {
  return request("/research/status");
}

export function grantResearchConsent(): Promise<{ granted: boolean }> {
  return request("/research/consent", { method: "POST" });
}

export function revokeResearchConsent(): Promise<void> {
  return request("/research/consent", { method: "DELETE" });
}

// --- Claims & evidence -----------------------------------------------------

export function listClaims(): Promise<Claim[]> {
  return request("/claims");
}

export function getClaim(claimId: string): Promise<Claim> {
  return request(`/claims/${claimId}`);
}

export function createClaim(payload: {
  label: string;
  topic: string;
  aliases: string[];
  phrases?: string[];
  trigger_terms?: string[];
  reading_query?: string;
}): Promise<Claim> {
  return request("/claims", { method: "POST", body: payload });
}

export function listVersions(claimId: string): Promise<EvidenceVersion[]> {
  return request(`/claims/${claimId}/versions`);
}

export interface CreateVersionPayload {
  evidence_strength: string;
  summary: string;
  qualifications: string;
  alternative_framing: string;
  source_links: Array<{ source_id: string; role: string }>;
  supersedes_version_id?: string | null;
}

export function createVersion(claimId: string, payload: CreateVersionPayload): Promise<EvidenceVersion> {
  return request(`/claims/${claimId}/versions`, { method: "POST", body: payload });
}

export function getVersion(versionId: string): Promise<EvidenceVersion> {
  return request(`/evidence-versions/${versionId}`);
}

export function submitForReview(versionId: string): Promise<EvidenceVersion> {
  return request(`/evidence-versions/${versionId}/submit-for-review`, { method: "POST" });
}

export function approveVersion(versionId: string): Promise<EvidenceVersion> {
  return request(`/evidence-versions/${versionId}/approve`, { method: "POST" });
}

export function createSource(payload: { citation: string; identifier?: string | null }): Promise<Source> {
  return request("/sources", { method: "POST", body: payload });
}

export function updateSource(sourceId: string, payload: { citation: string; identifier?: string | null }): Promise<Source> {
  return request(`/sources/${sourceId}`, { method: "PATCH", body: payload });
}

export function verifySource(sourceId: string): Promise<Source> {
  return request(`/sources/${sourceId}/verify`, { method: "POST" });
}

// --- Releases ----------------------------------------------------------

export function listReleases(): Promise<Release[]> {
  return request("/releases");
}

export function getCurrentRelease(): Promise<Release> {
  return request("/releases/current");
}

export function publishRelease(version: string): Promise<Release> {
  return request("/releases/publish", { method: "POST", body: { version } });
}

// --- Feedback ------------------------------------------------------------

export function createFeedback(payload: { kind: string; claim_id?: string | null; note?: string }): Promise<FeedbackItem> {
  return request("/feedback", { method: "POST", body: payload });
}

export function listFeedback(): Promise<FeedbackItem[]> {
  return request("/feedback");
}

export function triageFeedback(feedbackId: string, payload: { status: string; triage_note?: string }): Promise<FeedbackItem> {
  return request(`/feedback/${feedbackId}`, { method: "PATCH", body: payload });
}

// --- Orgs -----------------------------------------------------------------

export function createOrg(name: string): Promise<Organization> {
  return request("/orgs", { method: "POST", body: { name } });
}

export function getOrg(orgId: string): Promise<Organization> {
  return request(`/orgs/${orgId}`);
}

export function patchOrg(orgId: string, patch: Partial<Organization>): Promise<Organization> {
  return request(`/orgs/${orgId}`, { method: "PATCH", body: patch });
}

export function listOrgMembers(orgId: string): Promise<OrgMember[]> {
  return request(`/orgs/${orgId}/members`);
}

export function addOrgMember(orgId: string, payload: { user_id: string; role?: string }): Promise<OrgMember> {
  return request(`/orgs/${orgId}/members`, { method: "POST", body: payload });
}

export function removeOrgMember(orgId: string, userId: string): Promise<void> {
  return request(`/orgs/${orgId}/members/${userId}`, { method: "DELETE" });
}

export function getOrgAnalytics(orgId: string, windowDays = 30): Promise<OrgAnalyticsSummary> {
  return request(`/orgs/${orgId}/analytics?window_days=${windowDays}`);
}

// --- System admin -----------------------------------------------------------

export function listUsers(): Promise<AdminUser[]> {
  return request("/admin/users");
}

export function patchUser(userId: string, patch: { role?: string; is_active?: boolean }): Promise<AdminUser> {
  return request(`/admin/users/${userId}`, { method: "PATCH", body: patch });
}

export function getAuditLog(limit = 100): Promise<AuditLogEntry[]> {
  return request(`/admin/audit-log?limit=${limit}`);
}

// --- Analysis (not used directly by the web app today — the extension owns
// scanning — but kept here since AnalyzeRequest/AnalyzeResponse are part of
// the shared contract and a future in-browser "paste text to check" page
// would call this with no other changes needed). --------------------------

export function analyze(payload: AnalyzeRequest): Promise<AnalyzeResponse> {
  return request("/analyze", { method: "POST", body: payload });
}

// --- Public (no account needed) ------------------------------------------

export function listPublicEdufads(): Promise<PublicEdufadSummary[]> {
  return request("/public/edufads", { retryOn401: false });
}

export function getPublicEdufad(id: string): Promise<PublicEdufad> {
  return request(`/public/edufads/${encodeURIComponent(id)}`, { retryOn401: false });
}

export function searchResearch(q: string): Promise<ReadingSearchResult> {
  return request(`/public/research?q=${encodeURIComponent(q)}`, { retryOn401: false });
}

export function getCuratorReading(claimId: string): Promise<CuratorReading> {
  return request(`/claims/${claimId}/reading`);
}

export function setReadingStatus(claimId: string, itemId: string, status: "auto" | "pinned" | "hidden"): Promise<CuratorReading> {
  return request(`/claims/${claimId}/reading/${itemId}`, { method: "PATCH", body: { status } });
}

export function setReadingQuery(claimId: string, readingQuery: string): Promise<CuratorReading> {
  return request(`/claims/${claimId}/reading-query`, { method: "PUT", body: { reading_query: readingQuery } });
}

export function refreshReading(claimId: string): Promise<CuratorReading> {
  return request(`/claims/${claimId}/reading/refresh`, { method: "POST" });
}

export function getSiteConfig(): Promise<{ registration_open: boolean }> {
  return request("/public/site", { retryOn401: false });
}

export function createVerifiedAccount(payload: { email: string; display_name: string; role: string }): Promise<{
  user: AdminUser;
  temporary_password: string;
}> {
  return request("/admin/users", { method: "POST", body: payload });
}

export function changeMyPassword(currentPassword: string, newPassword: string): Promise<void> {
  return request("/me/password", { method: "POST", body: { current_password: currentPassword, new_password: newPassword } });
}

export function getPublicStats(): Promise<PublicStats> {
  return request("/public/stats", { retryOn401: false });
}

/** The text is analysed in memory and never stored. */
export function checkPassage(text: string): Promise<PassageCheckResponse> {
  return request("/public/check", { method: "POST", body: { text } });
}

export function getExtensionRelease(version?: string): Promise<ExtensionRelease> {
  const q = version ? `?version=${encodeURIComponent(version)}` : "";
  return request(`/extension/release${q}`, { retryOn401: false });
}

// --- Suggestions, dashboard, insights --------------------------------------

export function createSuggestion(body: { idea: string; where_seen: string; source_hint: string }): Promise<Suggestion> {
  return request("/suggestions", { method: "POST", body });
}

export function mySuggestions(): Promise<Suggestion[]> {
  return request("/suggestions/mine");
}

export function listSuggestions(): Promise<Suggestion[]> {
  return request("/suggestions");
}

export function reviewSuggestion(
  id: string,
  body: { status: "accepted" | "declined" | "duplicate"; review_note: string; linked_claim_id?: string | null },
): Promise<Suggestion> {
  return request(`/suggestions/${encodeURIComponent(id)}`, { method: "PATCH", body });
}

export function getMyDashboard(): Promise<MeDashboard> {
  return request("/me/dashboard");
}

export function getAdminInsights(): Promise<AdminInsights> {
  return request("/admin/insights");
}
